/**
 * Who gets an in-app notification for an OE Condition event (Beta-1 plan P2-17) — pure, tested.
 * Recipients are resolved from the organization's members (membersIndex → `{ uid, role, scopeId }`)
 * with the same routing as the queues (conditionWorkflow.routingFor).
 *
 * Event kinds (also the `type` stored on Hugo_Notifications):
 *   condition_new_l1        a new condition is in your L1 queue
 *   condition_l2_pending    an L1-approved item is in your L2 queue
 *   condition_returned      returned to L1 (L1 reviewer), or a resolution returned (owner)
 *   condition_assigned      you are the owner (person) / your department owns it (DH)
 *   resolution_pending      a resolution awaits your sign-off (L1, then L2)
 *   condition_status        your own submission changed status (entrant)
 *   admin_acted_as          an administrator acted on your behalf (P4-07)
 */
import { routingFor, isSingleStep } from './conditionWorkflow.js';

function scopeMatches(member, reviewerRole, condition, org) {
  switch (reviewerRole) {
    case 'team_lead':
      return member.role === 'team_lead' && member.scopeId === condition.nwtId;
    case 'department_lead':
      return member.role === 'department_lead' && member.scopeId === condition.deptId;
    case 'site_manager':
      return member.role === 'site_manager' && member.scopeId === condition.siteId;
    case 'org_admin':
      return member.role === 'org_admin' || (Array.isArray(org?.admins) && org.admins.includes(member.uid));
    default:
      return false;
  }
}

/** Members who hold a reviewer role for this condition (empty when nobody does — the queue still works for admins). */
export function reviewersFor(level, condition, members = [], org = null) {
  const routing = routingFor(condition.submitterRole, org?.reviewRouting || null);
  const role = level === 'l1' ? routing.l1 : routing.l2;
  if (!role) return [];
  return members.filter((m) => scopeMatches(m, role, condition, org));
}

/** Department Head(s) of the owner department + the assigned person. */
export function ownersFor(condition, members = []) {
  const out = [];
  if (condition.ownerUid) out.push(...members.filter((m) => m.uid === condition.ownerUid));
  if (condition.ownerDeptId) out.push(...members.filter((m) => m.role === 'department_lead' && m.scopeId === condition.ownerDeptId));
  return out;
}

function unique(list, exclude = []) {
  const seen = new Set(exclude.filter(Boolean));
  return list.filter((m) => m && m.uid && !seen.has(m.uid) && seen.add(m.uid));
}

/**
 * Notifications to write for one transition.
 * @returns {Array<{ uid, type, message }>}
 */
export function notificationsFor(event, condition, { members = [], org = null, actorUid = null, note = '' } = {}) {
  const id = condition.conditionId ? `${condition.conditionId} · ` : '';
  const title = `${id}${condition.title || 'OE Condition'}`;
  const single = isSingleStep(condition, org?.reviewRouting || null);
  const out = [];
  const push = (list, type, message) => {
    for (const m of unique(list, [actorUid])) out.push({ uid: m.uid, type, message });
  };
  const entrant = condition.submittedBy && condition.submittedBy !== actorUid ? [{ uid: condition.submittedBy }] : [];

  switch (event) {
    case 'created':
      push(reviewersFor('l1', condition, members, org), 'condition_new_l1', `New OE Condition to review: ${title}`);
      break;
    case 'l1_opened':
      push(entrant, 'condition_status', `${title} is now Review in Progress-L1`);
      break;
    case 'l1_approved':
      if (single) {
        push(entrant, 'condition_status', `${title} is Approved/Active`);
        push(ownersFor(condition, members), 'condition_assigned', `Your department owns ${title}`);
      } else {
        push(reviewersFor('l2', condition, members, org), 'condition_l2_pending', `L1-approved, awaiting your L2 approval: ${title}`);
        push(entrant, 'condition_status', `${title} is now Review in Progress-L2`);
      }
      break;
    case 'l2_approved':
      push(entrant, 'condition_status', `${title} is Approved/Active`);
      push(ownersFor(condition, members), 'condition_assigned', condition.ownerUid ? `You are the owner of ${title}` : `Your department owns ${title}`);
      break;
    case 'returned_to_l1':
      push(reviewersFor('l1', condition, members, org), 'condition_returned', `Returned to L1${note ? ` — ${note}` : ''}: ${title}`);
      push(entrant, 'condition_status', `${title} went back to Review in Progress-L1`);
      break;
    case 'not_approved':
      push(entrant, 'condition_status', `${title} was Not Approved${note ? ` — ${note}` : ''}`);
      break;
    case 'resolution_submitted':
      push(reviewersFor('l1', condition, members, org), 'resolution_pending', `Resolution awaiting your L1 sign-off: ${title}`);
      push(entrant, 'condition_status', `${title} is Resolution Pending Approval`);
      break;
    case 'resolution_l1_approved':
      if (!single) push(reviewersFor('l2', condition, members, org), 'resolution_pending', `Resolution awaiting your L2 sign-off: ${title}`);
      break;
    case 'resolved':
      push(entrant, 'condition_status', `${title} is Resolved`);
      push(ownersFor(condition, members), 'condition_status', `${title} is Resolved`);
      break;
    case 'resolution_returned':
      push(ownersFor(condition, members), 'condition_returned', `Resolution returned${note ? ` — ${note}` : ''}: ${title}`);
      break;
    case 'cancelled':
      push(entrant, 'condition_status', `${title} was cancelled by an administrator${note ? ` — ${note}` : ''}`);
      push([...reviewersFor('l1', condition, members, org), ...ownersFor(condition, members)], 'condition_status', `${title} was cancelled by an administrator`);
      break;
    case 'revised':
      push(entrant, 'condition_status', `A decision on ${title} was revised${note ? ` — ${note}` : ''}`);
      break;
    case 'owner_changed':
      push(ownersFor(condition, members), 'condition_assigned', condition.ownerUid ? `You are now the owner of ${title}` : `Your department now owns ${title}`);
      break;
    default:
      break;
  }
  return out;
}

/** P4-07: the member an admin acted for gets told. */
export function actedAsNotification(actingFor, actorName, what, condition) {
  if (!actingFor?.uid) return null;
  const id = condition?.conditionId ? `${condition.conditionId} · ` : '';
  return { uid: actingFor.uid, type: 'admin_acted_as', message: `${actorName || 'An administrator'} ${what} on your behalf: ${id}${condition?.title || ''}`.trim() };
}
